import { useState } from "react";
import { Switch } from "V2.0/common/switch";
import { Button } from "V2.0/common/button";
import InputComponent from "V2.0/components/InputComponent";
import { putTranscriptionSettings } from "../service";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";

function SpeakMessageLowConfidenceSwitch(props) {
  const {
    isSpeakMessageOnLowConfidenceEnabled,
    onToggleSpeakLowConfidenceMessage,
    disabled,
  } = props;

  return (
    <Switch
      color="emerald"
      checked={isSpeakMessageOnLowConfidenceEnabled}
      onClick={onToggleSpeakLowConfidenceMessage}
      disabled={disabled}
    />
  );
}

export default function ConfidenceSettings(props) {
  const {
    transcriptionSettings,
    workspaceId,
    assistantId,
    syncAssistant,
    locked,
  } = props;

  const [settings, setSettings] = useState({
    confidenceThreshold:
      transcriptionSettings?.deepgramSettings?.confidenceThreshold * 100,
    lowConfidenceMessageEnabled:
      transcriptionSettings?.lowConfidenceMessageEnabled,
    lowConfidenceMessage: transcriptionSettings?.lowConfidenceMessage,
  });

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    lowConfidenceMessage: null,
  });

  function handleError(error) {
    setErrors((prevErrors) => {
      return {
        ...prevErrors,
        ...error,
      };
    });
  }

  function handleUpdateConfidenceThreshold(e) {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        confidenceThreshold: e.currentTarget.value,
      };
    });
  }

  function handleToggleSpeakMessageOnLowConfidence() {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        lowConfidenceMessageEnabled: !prevSettings.lowConfidenceMessageEnabled,
      };
    });
  }

  function handleUpdateLowConfidenceMessage(e) {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        lowConfidenceMessage: e.target.value,
      };
    });
  }

  async function handleSave() {
    const { lowConfidenceMessage, lowConfidenceMessageEnabled } = settings;
    const payload = {
      deepgramSettings: {
        confidenceThreshold: settings.confidenceThreshold / 100,
      },
      lowConfidenceMessage,
      lowConfidenceMessageEnabled,
    };
    message.loading("Updating Settings");

    try {
      const response = await putTranscriptionSettings({
        workspaceId,
        assistantId,
        payload,
      });

      if (response.status === 200) {
        message.destroy();
        message.success("Settings Updated");
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            transcriptionSettings: response.data,
          };
        });
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to update Confidence Settings");
    }
  }

  const isError = Object.values(errors).some((error) => Boolean(error));

  return (
    <div className="flex flex-col w-3/5 min-w-[600px] gap-y-8 border rounded-lg m-4">
      <div className="px-6 py-4 bg-gray-50 rounded-t-lg">
        <p className="text-md font-semibold text-gray-900">
          Speech Uncertainity
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Control the assistant response when it doesn’t understand what the
          user has said
        </p>
      </div>

      <div className="flex flex-col space-y-4 px-8">
        <div className="grid grid-cols-3 ">
          <div className="flex gap-x-2 items-center col-span-3">
            <p className="text-md font-medium">
              Clarify When Speech is Uncertain
            </p>
            <SpeakMessageLowConfidenceSwitch
              isSpeakMessageOnLowConfidenceEnabled={
                settings.lowConfidenceMessageEnabled
              }
              onToggleSpeakLowConfidenceMessage={
                handleToggleSpeakMessageOnLowConfidence
              }
              disabled={locked}
            />
          </div>
        </div>

        <div className="px-6">
          <div className="grid grid-cols-4">
            <div className="col-span-4">
              <InputComponent
                type="slider"
                disabled={!settings.lowConfidenceMessageEnabled || locked}
                label="Confidence Level"
                description="The assistant clarifies when it's confidence in the user’s speech is below this value"
                value={settings.confidenceThreshold}
                min={0}
                max={100}
                step={10}
                sliderMarks={{
                  0: "0%",
                  50: "50%",
                  100: "100%",
                }}
                onChange={handleUpdateConfidenceThreshold}
              />
            </div>
          </div>

          <div className="grid grid-cols-4">
            <div className="col-span-4">
              <InputComponent
                description="The message the assistant says to request for clarification"
                disabled={!settings.lowConfidenceMessageEnabled || locked}
                type="textArea"
                style={{ height: "100px", fontSize: "13px" }}
                label="Low Confidence Message"
                value={settings.lowConfidenceMessage}
                onChange={handleUpdateLowConfidenceMessage}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 px-6 py-3 text-right rounded-b-lg">
        {!locked && (
          <Button
            loading={loading}
            onClick={handleSave}
            disabled={isError}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}
